import {
	addDays,
	addMonths,
	compareAsc,
	differenceInDays,
	format,
	isValid,
	parseISO,
	startOfDay,
	subYears,
} from 'date-fns';
import { es } from 'date-fns/locale';

export const TRAVEL_DATE_MAX_MONTHS_AHEAD = 12;

export const TRAVEL_DATE_INPUT_MIN = '2000-01-01';
export const TRAVEL_DATE_INPUT_MAX = '2099-12-31';

export function parseTravelDateValue(value: string | null | undefined): Date | null {
	if (!value || typeof value !== 'string') {
		return null;
	}

	const trimmed = value.trim();
	if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
		return null;
	}

	const date = parseISO(trimmed);
	return isValid(date) ? startOfDay(date) : null;
}

/** Soporta nombres anidados de react-hook-form (ej. transferSegments.0.startDate). */
export function isFieldDirty(dirtyFields: Record<string, unknown> | undefined, fieldName: string): boolean {
	if (!dirtyFields || !fieldName) {
		return false;
	}

	const segments = fieldName.split('.');
	let current: unknown = dirtyFields;

	for (const segment of segments) {
		if (current == null || typeof current !== 'object') {
			return false;
		}
		current = (current as Record<string, unknown>)[segment];
	}

	return current === true;
}

export type TravelDateDisclaimerOptions = {
	/** Pasado / +12 meses / año raro. Solo al editar, no al abrir servicios guardados. */
	validateRanges?: boolean;
};

/** Aviso blando: no bloquea guardado; se muestra en rojo junto al campo. */
export function getTravelDateDisclaimer(
	value: string | null | undefined,
	options?: TravelDateDisclaimerOptions,
): string | null {
	if (!value || typeof value !== 'string') {
		return null;
	}

	if (options?.validateRanges !== true) {
		return null;
	}

	const yearMatch = value.match(/^(\d{4})-/);
	if (yearMatch) {
		const year = Number.parseInt(yearMatch[1], 10);
		if (year < 2000 || year > 2099) {
			return 'Revisá el año: parece incorrecto.';
		}
	}

	const date = parseTravelDateValue(value);
	if (!date) {
		return null;
	}

	const today = startOfDay(new Date());
	const maxAhead = startOfDay(addMonths(today, TRAVEL_DATE_MAX_MONTHS_AHEAD));

	if (compareAsc(date, today) < 0) {
		return 'La fecha es anterior a hoy.';
	}

	if (compareAsc(date, maxAhead) > 0) {
		return `La fecha supera ${TRAVEL_DATE_MAX_MONTHS_AHEAD} meses desde hoy.`;
	}

	return null;
}

export const BIRTHDATE_MAX_AGE_YEARS = 100;

export function getBirthdateInputMin(): string {
	return format(startOfDay(subYears(new Date(), BIRTHDATE_MAX_AGE_YEARS)), 'yyyy-MM-dd');
}

export function getBirthdateInputMax(): string {
	return format(startOfDay(new Date()), 'yyyy-MM-dd');
}

/** Aviso blando para fecha de nacimiento: futuro o más de 100 años. */
export function getBirthdateDisclaimer(
	value: string | null | undefined,
	options?: TravelDateDisclaimerOptions,
): string | null {
	if (!value || options?.validateRanges !== true) {
		return null;
	}

	const date = parseTravelDateValue(value);
	if (!date) {
		return null;
	}

	const today = startOfDay(new Date());

	if (compareAsc(date, today) > 0) {
		return 'La fecha no puede ser posterior a hoy.';
	}

	const oldestAllowed = startOfDay(subYears(today, BIRTHDATE_MAX_AGE_YEARS));
	if (compareAsc(date, oldestAllowed) < 0) {
		return 'Revisá la fecha: la edad supera los 100 años.';
	}

	return null;
}

export function getDocumentExpirationInputMin(): string {
	return format(addDays(startOfDay(new Date()), 1), 'yyyy-MM-dd');
}

export function getDocumentExpirationInputMax(): string {
	return TRAVEL_DATE_INPUT_MAX;
}

/** Vencimiento de documento: debe ser posterior a hoy (sin tope de 12 meses). */
export function getDocumentExpirationDisclaimer(
	value: string | null | undefined,
	options?: TravelDateDisclaimerOptions,
): string | null {
	if (!value || options?.validateRanges !== true) {
		return null;
	}

	const date = parseTravelDateValue(value);
	if (!date) {
		return null;
	}

	const today = startOfDay(new Date());
	if (compareAsc(date, today) <= 0) {
		return 'La fecha debe ser posterior a hoy.';
	}

	return null;
}

export function formatTravelDateLongSpanish(value: string | null | undefined): string | null {
	const date = parseTravelDateValue(value);
	if (!date) {
		return null;
	}

	return format(date, 'd \'de\' MMMM \'de\' yyyy', { locale: es });
}

export type StayDurationSeverity = 'info' | 'warning' | 'error';

export type StayDurationSummary = {
	days: number;
	nights: number;
	text: string;
	severity: StayDurationSeverity;
};

/**
 * Noches = días entre check-in y check-out (fin exclusivo).
 * Días en calendario = noches + 1 (inclusivo).
 */
export function getStayDurationSummary(
	startDate: string | null | undefined,
	endDate: string | null | undefined,
	options?: { variant?: 'accommodation' | 'transfer' },
): StayDurationSummary | null {
	const startLong = formatTravelDateLongSpanish(startDate);
	const endLong = formatTravelDateLongSpanish(endDate);
	const start = parseTravelDateValue(startDate);
	const end = parseTravelDateValue(endDate);

	if (!start || !end || !startLong || !endLong) {
		return null;
	}

	const nights = differenceInDays(end, start);
	const variant = options?.variant ?? 'accommodation';

	if (nights < 0) {
		return {
			days: 0,
			nights,
			text: 'La fecha final es anterior a la inicial.',
			severity: 'error',
		};
	}

	const days = nights + 1;

	if (variant === 'transfer') {
		if (nights === 0) {
			return {
				days: 1,
				nights: 0,
				text: `Mismo día (${startLong})`,
				severity: 'info',
			};
		}

		return {
			days,
			nights,
			text: `${days} días (del ${startLong} al ${endLong})`,
			severity: 'info',
		};
	}

	return {
		days,
		nights,
		text: `${days} días / ${nights} noches (del ${startLong} al ${endLong})`,
		severity: nights === 0 ? 'warning' : 'info',
	};
}
